/**
 * Zoom recording/transcript status chip shown beside the progress note title after the
 * session ends and while the note is still unsigned. Hidden before session end (idle).
 */
import { Badge } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';
import type { Appointment } from '@medplum/fhirtypes';

import { resolveZoomTranscriptUiState } from '@/lib/aiNoteGenerationService';

export interface TranscriptStatusBadgeProps {
  appointment?: Appointment;
  sessionEnded: boolean;
}

export function TranscriptStatusBadge({
  appointment,
  sessionEnded,
}: TranscriptStatusBadgeProps): JSX.Element | null {
  const state = resolveZoomTranscriptUiState({ appointment, sessionEnded });

  switch (state) {
    case 'ready':
      return (
        <Badge size="sm" variant="light" color="green" leftSection={<IconCheck size={10} />}>
          Transcript ready
        </Badge>
      );
    case 'processing':
      return (
        <Badge size="sm" variant="light" color="yellow" leftSection={<IconClock size={10} />}>
          Transcript processing…
        </Badge>
      );
    case 'missing':
      return (
        <Badge size="sm" variant="light" color="red" leftSection={<IconAlertCircle size={10} />}>
          Transcript missing
        </Badge>
      );
    case 'no_recording':
      return (
        <Badge size="sm" variant="light" color="gray" leftSection={<IconAlertCircle size={10} />}>
          No recording
        </Badge>
      );
    case 'expired':
      return (
        <Badge size="sm" variant="light" color="gray" leftSection={<IconAlertCircle size={10} />}>
          Transcript expired
        </Badge>
      );
    case 'idle':
    default:
      // Zoom only produces a transcript after the meeting ends — hide the badge until then.
      return null;
  }
}
